'use client';

import NextLink from 'next/link';
import { Icon } from '@vespaai/components';
import type { ComponentProps, ReactNode } from 'react';

type LinkVariant = 'internal' | 'external' | 'documentation' | 'download' | 'auth';

type BaseLinkProps = Omit<ComponentProps<typeof NextLink>, 'href' | 'download'> & {
  href: string;
};

// `variant="download"` requires a `download` attribute — without it the browser
// just navigates instead of downloading. Other variants must not pass `download`
// (use `variant="download"` to express download intent).
type LinkProps = BaseLinkProps &
  (
    | { variant?: Exclude<LinkVariant, 'download'>; download?: never }
    | { variant: 'download'; download: boolean | string }
  );

export const isInternalLink = (link?: string): boolean => {
  if (!link) return false;
  // Matches URI schemes (e.g. https:, mailto:, tel:) and protocol-relative URLs (//)
  return !/^[a-z][a-z0-9+.-]*:|^\/\//.test(link);
};

const isAuthLink = (link: string): boolean => link.startsWith('/auth/');

type VariantConfig = {
  renderAs: 'next' | 'anchor';
  icon?: ReactNode;
  defaultProps?: ComponentProps<'a'>;
};

const linkVariants: Record<LinkVariant, VariantConfig> = {
  internal: { renderAs: 'next' },
  external: {
    renderAs: 'anchor',
    defaultProps: { target: '_blank', rel: 'noopener noreferrer' },
  },
  documentation: {
    renderAs: 'anchor',
    icon: <Icon name="circle-book-open" />,
    defaultProps: { target: '_blank', rel: 'noopener noreferrer' },
  },
  download: { renderAs: 'anchor' },
  // Auth routes are server-side redirects, not React pages — render as <a> to skip RSC
  auth: { renderAs: 'anchor' },
};

function inferVariant(href: string): Exclude<LinkVariant, 'download'> {
  if (isAuthLink(href)) return 'auth';
  if (!isInternalLink(href)) return 'external';
  return 'internal';
}

export function Link({ href = '', variant, prefetch, children, ...props }: LinkProps): ReactNode {
  const config = linkVariants[variant ?? inferVariant(href)];

  if (config.renderAs === 'next') {
    return (
      <NextLink href={href} prefetch={prefetch} {...props}>
        {children}
      </NextLink>
    );
  }

  return (
    <a href={href} {...config.defaultProps} {...props}>
      {children}
      {config.icon}
    </a>
  );
}
